/**
 * Kenmore 7510 Sample Data
 * Construction project for Maven Builder organization with sub-division and multi-house development
 */

import type { Project, Task, Epic, Sprint, Priority, TaskStatus } from '../types';

const ORG_ID = 'org_intellespheremaven-builder';
const PROJECT_ID = 'proj-kenmore-7510';

export const kenmore7510Project: Project = {
  id: PROJECT_ID,
  organizationId: ORG_ID,
  name: 'Kenmore 7510',
  description: 'Multi-lot residential construction project in Kenmore including sub-division, engineering, and three-house development',
  status: 'In Progress',
  proposal: `# Kenmore 7510 - Construction Project

## Overview
Comprehensive residential construction project involving land acquisition, sub-division, engineering work, permits, and construction of three houses on separate lots.

## Key Objectives
- Land Acquisition & Sub-division
- Engineering (Civil, Lateral, Geotechnical)
- Design & Permits for Three Houses
- Pre-Construction Work (Demolition, Utilities, Site Prep)
- Foundation & Framing
- Exterior Shell & Rough-ins
- Interior Finishes
- Landscaping & Final Inspections
- Sales & Marketing

## Current Status
The project is in progress with land acquisition nearly complete, sub-division work underway, and engineering firms selected.`,
  costs: [],
  teamMembers: [
    { userId: 'user-masud-mb', role: 'stakeholder' },
    { userId: 'asif.redmond@gmail.com-mb', role: 'stakeholder' },
    { userId: 'user-hafiz-mb', role: 'developer' },
    { userId: 'aidimentry', role: 'developer' },
    { userId: 'user-richard-mb', role: 'external_partner' },
    { userId: 'user-feras-mb', role: 'external_partner' },
  ],
  createdAt: '2024-08-01T00:00:00Z',
  updatedAt: '2026-02-23T00:00:00Z',
  externalLinks: [
    { type: 'Engineering', url: 'http://www.eastsideconsultants.com/contact/index.html' },
    { type: 'Engineering', url: 'https://www.plogengineering.com/' },
    { type: 'Engineering', url: 'https://www.goldsmithengineering.com/' },
  ],
  startDate: '2024-08-01',
  targetEndDate: '2026-12-31',
};

// Epic definitions based on construction phases
export const kenmore7510Epics: Epic[] = [
  {
    id: 'epic-k7510-land',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Land Acquisition',
    description: 'Complete land acquisition including surveys, legal work, and financial arrangements',
    status: 'In Progress',
    taskIds: [],
    standupNotes: [],
    createdAt: '2024-08-01T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-k7510-subplat',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Subplat & Engineering',
    description: 'Sub-division planning, engineering surveys, geotechnical work, civil drainage, and lateral engineering',
    status: 'In Progress',
    taskIds: [],
    standupNotes: [],
    createdAt: '2024-08-15T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-k7510-subplat-s',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Subplat & Engineering:S',
    description: 'Specialized surveys and geotechnical testing',
    status: 'Active',
    taskIds: [],
    standupNotes: [],
    createdAt: '2024-08-15T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-k7510-tree',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Hazaordous Tree Removal',
    description: 'Tree survey, hazardous tree removal, and replantation',
    status: 'Completed',
    taskIds: [],
    standupNotes: [],
    createdAt: '2024-08-11T00:00:00Z',
    updatedAt: '2024-09-15T00:00:00Z',
  },
  {
    id: 'epic-k7510-replant',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Replantation',
    description: 'Replace exceptional trees as required by permits',
    status: 'Completed',
    taskIds: [],
    standupNotes: [],
    createdAt: '2024-09-01T00:00:00Z',
    updatedAt: '2024-09-20T00:00:00Z',
  },
  {
    id: 'epic-k7510-design',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Design & Permits',
    description: 'Architectural design for three houses, fence design, and building permits',
    status: 'In Progress',
    taskIds: [],
    standupNotes: [],
    createdAt: '2024-08-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
  },
  {
    id: 'epic-k7510-precon',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Pre-Construction',
    description: 'Site preparation including demolition, utilities, earthwork, fencing, and yard work',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-foundation',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Foundation',
    description: 'Foundation work including concrete pours for three houses, driveways, and patios',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-03-01T00:00:00Z',
    updatedAt: '2025-03-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-framing',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Framing',
    description: 'Framing work including lumber, labor, and metal base',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-05-01T00:00:00Z',
    updatedAt: '2025-05-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-exterior',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Exterior Shell',
    description: 'Exterior work including roofing, windows, siding, gutters, and doors',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-06-01T00:00:00Z',
    updatedAt: '2025-06-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-roughin',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Rough-ins',
    description: 'Plumbing, electrical, and HVAC rough-ins with utility connections',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-07-01T00:00:00Z',
    updatedAt: '2025-07-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-insulation',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Insulation & Drywall',
    description: 'Insulation and drywall installation',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-08-01T00:00:00Z',
    updatedAt: '2025-08-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-interior',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Interior Finishes',
    description: 'Interior finishes including cabinets, countertops, fixtures, flooring, paint, and appliances',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2025-09-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-finishing',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Finishing Touches',
    description: 'Final inspections, landscaping, and fence installation',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-08-01T00:00:00Z',
    updatedAt: '2026-08-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-sales',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Sales & Marketing',
    description: 'Marketing, staging, and closing sales for three houses',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2026-09-01T00:00:00Z',
    updatedAt: '2026-09-01T00:00:00Z',
  },
  {
    id: 'epic-k7510-misc',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Misc',
    description: 'Miscellaneous tasks including garbage removal, maintenance, and extra work',
    status: 'Not Started',
    taskIds: [],
    standupNotes: [],
    createdAt: '2024-08-01T00:00:00Z',
    updatedAt: '2024-08-01T00:00:00Z',
  },
];

// Construction tasks
export const kenmore7510Tasks: Task[] = [
  // Land Acquisition Tasks
  {
    id: 'K7510-006',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Transfer cash from stock accounts',
    description: '',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    category: 'Land Acquisition',
    assigneeIds: ['user-masud-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-land',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-05T00:00:00Z',
    updatedAt: '2024-08-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-009',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Sub-division',
    description: '',
    type: 'Feature',
    status: 'In Progress',
    priority: 'High',
    category: 'Land Acquisition',
    assigneeIds: ['user-hafiz-mb', 'user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-land',
    estimatedDays: 30,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-15T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-010',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Tree survey',
    description: '',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    category: 'Land Acquisition',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-land',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-11T00:00:00Z',
    updatedAt: '2024-08-13T00:00:00Z',
    targetEndDate: '2024-08-13',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-011',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Lawyer',
    description: 'Free consultation',
    type: 'Feature',
    status: 'Not Required',
    priority: 'Low',
    category: 'Land Acquisition',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-land',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-11T00:00:00Z',
    updatedAt: '2024-08-11T00:00:00Z',
    notes: 'https://www.lissaklaw.com/',
    targetEndDate: '2024-08-11',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Website', url: 'https://www.lissaklaw.com/' }],
    changeLogs: [],
  },
  {
    id: 'K7510-012',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Geological survey',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Land Acquisition',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-land',
    estimatedDays: 3,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-15T00:00:00Z',
    updatedAt: '2024-08-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-015',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Masud to clear 20K by Sep 15 with Asif',
    description: '',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    category: 'Land Acquisition',
    assigneeIds: ['user-masud-mb', 'asif.redmond@gmail.com-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-land',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-09-10T00:00:00Z',
    updatedAt: '2024-09-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-018',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Look 2/3 engineering firm to talk next week for sub-division',
    description: '',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    category: 'Land Acquisition',
    assigneeIds: ['user-hafiz-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-land',
    estimatedDays: 3,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-18T00:00:00Z',
    updatedAt: '2024-08-25T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Subplat & Engineering Tasks
  {
    id: 'K7510-004',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Demolition permit',
    description: 'Demolition folks will take care of it',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    category: 'Subplat & Engineering',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-09-10T00:00:00Z',
    updatedAt: '2024-09-15T00:00:00Z',
    targetEndDate: '2024-09-15',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-018-1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Review engineering firms',
    description: '* http://www.eastsideconsultants.com/contact/index.html\n* https://www.plogengineering.com/\n* https://www.goldsmithengineering.com/\n* https://www.encompasses.net/#slideMain\n* https://www.goldsmithengineering.com/',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-hafiz-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 5,
    notes: 'Selected PLOG',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-20T00:00:00Z',
    updatedAt: '2024-08-28T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [
      { type: 'Engineering Firm', url: 'http://www.eastsideconsultants.com/contact/index.html' },
      { type: 'Engineering Firm', url: 'https://www.plogengineering.com/' },
      { type: 'Engineering Firm', url: 'https://www.goldsmithengineering.com/' },
      { type: 'Engineering Firm', url: 'https://www.encompasses.net/#slideMain' },
    ],
    changeLogs: [],
  },
  {
    id: 'K7510-019',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Survey',
    description: '',
    type: 'Feature',
    status: 'Active',
    priority: 'High',
    category: 'Subplat & Engineering:S',
    assigneeIds: ['user-richard-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat-s',
    estimatedDays: 5,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-09-01T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-020',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Additional Land Survey',
    description: '',
    type: 'Feature',
    status: 'Active',
    priority: 'Medium',
    category: 'Subplat & Engineering:S',
    assigneeIds: ['user-richard-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat-s',
    estimatedDays: 3,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-09-05T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-021',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Geotech (Soil Test)',
    description: 'Stu Swenson • sswenson@aartesting.com\nPOC: Stu Swenson\nLaboratory Services Manager / Project Manager\nAAR Testing and Inspection, Inc.\nwww.aartesting.com\n206-214-7899 (C)',
    type: 'Feature',
    status: 'Active',
    priority: 'High',
    category: 'Subplat & Engineering:S',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat-s',
    estimatedDays: 7,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-09-10T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [{ type: 'Vendor', url: 'https://www.aartesting.com' }],
    changeLogs: [],
  },
  {
    id: 'K7510-022',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Additional Geotech',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Subplat & Engineering:S',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat-s',
    estimatedDays: 5,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-09-20T00:00:00Z',
    updatedAt: '2024-09-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-023',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Civil drainage engineering',
    description: '',
    type: 'Feature',
    status: 'Active',
    priority: 'High',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-feras-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 15,
    notes: 'PLOG',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-10-01T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-024',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Lateral engineering',
    description: '',
    type: 'Feature',
    status: 'Active',
    priority: 'High',
    category: 'Subplat & Engineering',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 10,
    notes: 'SDA',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-10-05T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-025',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Subplat: Pre-application',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-hafiz-mb', 'user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 7,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-11-01T00:00:00Z',
    updatedAt: '2024-11-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-026',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Subplat: Application',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-hafiz-mb', 'user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 14,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-11-15T00:00:00Z',
    updatedAt: '2024-11-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Tree Removal Tasks
  {
    id: 'K7510-016',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Hazardous Tree Inspection',
    description: 'Inspection to be called',
    type: 'Feature',
    status: 'Completed',
    priority: 'High',
    category: 'Hazaordous Tree Removal',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-tree',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-15T00:00:00Z',
    updatedAt: '2024-08-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-016-1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Replant 1 tree to replace hazaordous exceptional tree as required by the permit',
    description: 'Consult engineer\nConsult city',
    type: 'Feature',
    status: 'Completed',
    priority: 'Medium',
    category: 'Replantation',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-replant',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-09-01T00:00:00Z',
    updatedAt: '2024-09-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Design & Permits Tasks
  {
    id: 'K7510-026-1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Fence',
    description: '* Request Puget Sound to close the brash task - it will help installing fence and ask Kaitlyn to close code correction letter',
    type: 'Feature',
    status: 'In Progress',
    priority: 'Medium',
    category: 'Design & Permits',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-design',
    estimatedDays: 5,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-10-01T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-027',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Architect',
    description: 'Meet on next Wednesday (Aug 28)',
    type: 'Feature',
    status: 'Active',
    priority: 'High',
    category: 'Design & Permits',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-design',
    estimatedDays: 10,
    notes: 'Lauren',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-08-20T00:00:00Z',
    updatedAt: '2026-02-23T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-028',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'House 1 design',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Design & Permits',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-design',
    estimatedDays: 20,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-11-01T00:00:00Z',
    updatedAt: '2024-11-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-029',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'House 2 design',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Design & Permits',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-design',
    estimatedDays: 20,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-11-01T00:00:00Z',
    updatedAt: '2024-11-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-030',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'House 3 design',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Design & Permits',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-design',
    estimatedDays: 20,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2024-11-01T00:00:00Z',
    updatedAt: '2024-11-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-031',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Building permit',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Design & Permits',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-design',
    estimatedDays: 30,
    notes: 'City of Redmond',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Pre-Construction Tasks
  {
    id: 'K7510-032',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Demolition-Concrete-Utility-Earthwork-1',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 10,
    notes: 'Alex Barba',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-01T00:00:00Z',
    updatedAt: '2025-02-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-033',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Demolition-Concrete-Utility-Earthwork-2',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 10,
    notes: 'Alex Barba',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-01T00:00:00Z',
    updatedAt: '2025-02-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-034',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Demolition-Concrete-Utility-Earthwork-3',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 10,
    notes: 'Alex Barba',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-01T00:00:00Z',
    updatedAt: '2025-02-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-035',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Rodent Abatement',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 2,
    notes: 'Eastside Exterminators',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-01T00:00:00Z',
    updatedAt: '2025-02-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-036',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Asbestos Survey',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 3,
    notes: 'PNWIG',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-05T00:00:00Z',
    updatedAt: '2025-02-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-037',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'PS Clean Air',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 2,
    notes: 'PS',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-10T00:00:00Z',
    updatedAt: '2025-02-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-038',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Tree Removal',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 5,
    notes: 'Juan, Susie',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-15T00:00:00Z',
    updatedAt: '2025-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-039',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Temp Pole',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 3,
    notes: 'Joe',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-20T00:00:00Z',
    updatedAt: '2025-02-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-040',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Backflow Test',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 1,
    notes: 'Stop Backflow',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-22T00:00:00Z',
    updatedAt: '2025-02-22T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-041',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Sewer video',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 1,
    notes: 'Dirtcheap',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-02-25T00:00:00Z',
    updatedAt: '2025-02-25T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-042',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Pipes + Sidewalk + Garbage',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 7,
    notes: 'Sergio',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-01T00:00:00Z',
    updatedAt: '2025-03-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-043',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Dirtcheap',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 5,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-05T00:00:00Z',
    updatedAt: '2025-03-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-044',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Yard work',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 3,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-10T00:00:00Z',
    updatedAt: '2025-03-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-045',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Yard Materials',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 2,
    notes: 'Plants - Kent East Hill Nursery',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-12T00:00:00Z',
    updatedAt: '2025-03-12T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-046',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Concrete for retaining wall (50 yards)',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-15T00:00:00Z',
    updatedAt: '2025-03-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-047',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Retaining wall labor',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 5,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-18T00:00:00Z',
    updatedAt: '2025-03-18T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-048',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Driveway Gate',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 3,
    notes: 'PNW Fence & Gate',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-20T00:00:00Z',
    updatedAt: '2025-03-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-049',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Fence',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 5,
    notes: 'National',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-22T00:00:00Z',
    updatedAt: '2025-03-22T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-050',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Fence Material',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Pre-Construction',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-precon',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-22T00:00:00Z',
    updatedAt: '2025-03-22T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Foundation Tasks - continuing with remaining construction phases
  {
    id: 'K7510-051',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Pour concretes',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Foundation',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-foundation',
    estimatedDays: 10,
    notes: 'Alex Barba, Concrete specialist of Seattle',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-04-01T00:00:00Z',
    updatedAt: '2025-04-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-052',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Concrete - Driveway and Patios',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Foundation',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-foundation',
    estimatedDays: 7,
    notes: 'Concrete specialist of Seattle',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-04-15T00:00:00Z',
    updatedAt: '2025-04-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Framing Tasks
  {
    id: 'K7510-053',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Lumber + Framing (Material)',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Framing',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-framing',
    estimatedDays: 3,
    notes: 'BMC, Dunn Lumber',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-05-01T00:00:00Z',
    updatedAt: '2025-05-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-054',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Framing (labor)',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Framing',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-framing',
    estimatedDays: 15,
    notes: 'Sergio',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-05-05T00:00:00Z',
    updatedAt: '2025-05-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-055',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Framing Extra mterial',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Framing',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-framing',
    estimatedDays: 2,
    notes: 'Sergio, Dunn Lumber',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-05-20T00:00:00Z',
    updatedAt: '2025-05-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-056',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Metal Base',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Framing',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-framing',
    estimatedDays: 3,
    notes: 'S&S Iron Works',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-05-25T00:00:00Z',
    updatedAt: '2025-05-25T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Exterior Shell Tasks
  {
    id: 'K7510-057',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Roof',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Exterior Shell',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-exterior',
    estimatedDays: 10,
    notes: 'Billy, AXIO, CASH',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-06-01T00:00:00Z',
    updatedAt: '2025-06-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-058',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Windows + Slidig Doors',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Exterior Shell',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-exterior',
    estimatedDays: 7,
    notes: 'Dunn Lumber',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-06-05T00:00:00Z',
    updatedAt: '2025-06-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-059',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Install sidings',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Exterior Shell',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-exterior',
    estimatedDays: 8,
    notes: 'Jose, Rainier Building Supply',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-06-15T00:00:00Z',
    updatedAt: '2025-06-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-060',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Gutter',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Exterior Shell',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-exterior',
    estimatedDays: 3,
    notes: 'Alliance',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-06-25T00:00:00Z',
    updatedAt: '2025-06-25T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-061',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Big Doors Install',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Exterior Shell',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-exterior',
    estimatedDays: 2,
    notes: 'Big Door Company',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-06-28T00:00:00Z',
    updatedAt: '2025-06-28T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-062',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Pivot Door',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Exterior Shell',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-exterior',
    estimatedDays: 2,
    notes: 'Pivot Door Company',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-06-30T00:00:00Z',
    updatedAt: '2025-06-30T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Rough-ins Tasks
  {
    id: 'K7510-063',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Plumbing Roughin & inspection',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Rough-ins',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-roughin',
    estimatedDays: 10,
    notes: 'Kyle - Lakeside Plumbing, For Alex Barba',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-07-01T00:00:00Z',
    updatedAt: '2025-07-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-064',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Electrical Roughin & inspection',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Rough-ins',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-roughin',
    estimatedDays: 10,
    notes: 'Dickson',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-07-05T00:00:00Z',
    updatedAt: '2025-07-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-065',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Heating ducts Roughin & inspection',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Rough-ins',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-roughin',
    estimatedDays: 7,
    notes: "Bob's",
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-07-10T00:00:00Z',
    updatedAt: '2025-07-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-066',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Gas connection',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Rough-ins',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-roughin',
    estimatedDays: 2,
    notes: 'PSE',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-07-18T00:00:00Z',
    updatedAt: '2025-07-18T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-067',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Electricity Connection',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Rough-ins',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-roughin',
    estimatedDays: 3,
    notes: 'PSE',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-07-20T00:00:00Z',
    updatedAt: '2025-07-20T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-068',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Utility work for Electricity',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Rough-ins',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-roughin',
    estimatedDays: 5,
    notes: 'NETVISION',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-07-22T00:00:00Z',
    updatedAt: '2025-07-22T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Insulation & Drywall Tasks
  {
    id: 'K7510-069',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Insulation',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Insulation & Drywall',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-insulation',
    estimatedDays: 7,
    notes: 'JTS Insulation',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-01T00:00:00Z',
    updatedAt: '2025-08-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-070',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Drywalls',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Insulation & Drywall',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-insulation',
    estimatedDays: 12,
    notes: "Sergio, De Haro's",
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-08-10T00:00:00Z',
    updatedAt: '2025-08-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Interior Finishes Tasks (abbreviated for length - adding key ones)
  {
    id: 'K7510-071',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Cabinets',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Interior Finishes',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-interior',
    estimatedDays: 7,
    notes: 'Seattle Cabinets, MISC, Ikea, Pantry',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2025-09-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-085',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Paint',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Interior Finishes',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-interior',
    estimatedDays: 10,
    notes: 'Eagle Pro',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-10-15T00:00:00Z',
    updatedAt: '2025-10-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Finishing Touches Tasks
  {
    id: 'K7510-093',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Inspections',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Finishing Touches',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-finishing',
    estimatedDays: 5,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-08-01T00:00:00Z',
    updatedAt: '2026-08-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-094',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Landscaping',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Finishing Touches',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-finishing',
    estimatedDays: 10,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-08-10T00:00:00Z',
    updatedAt: '2026-08-10T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Sales & Marketing Tasks
  {
    id: 'K7510-097',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Put Redfin listings',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Sales & Marketing',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-sales',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-09-01T00:00:00Z',
    updatedAt: '2026-09-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-098',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Staging',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Sales & Marketing',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-sales',
    estimatedDays: 5,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-09-05T00:00:00Z',
    updatedAt: '2026-09-05T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-099',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Closings',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Sales & Marketing',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-sales',
    estimatedDays: 7,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-09-15T00:00:00Z',
    updatedAt: '2026-09-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  // Misc Tasks
  {
    id: 'K7510-100',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Garbage Removal',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    category: 'Misc',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-misc',
    estimatedDays: 2,
    notes: "Sergio's guy, Christiano, King County Solid Waste",
    timeLogs: [],
    standupNotes: [],
    createdAt: '2025-03-01T00:00:00Z',
    updatedAt: '2025-03-01T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];

// Sprint with subplat review tasks
export const kenmore7510SubplatSprint: Sprint = {
  id: 'sprint-k7510-subplat-review',
  organizationId: ORG_ID,
  projectId: PROJECT_ID,
  name: 'Subplat Review & Engineering Fixes - Sprint 1',
  startDate: '2026-02-15',
  endDate: '2026-03-15',
  status: 'Active',
  taskIds: [],
  goals: 'Address engineering review comments including set backs, CRZ fixes, tree credit calculations, lot sizes, and engineering details for sub-division approval.',
};

export const kenmore7510SprintTasks: Task[] = [
  {
    id: 'K7510-A2-3',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Address Lot2 Set back',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-hafiz-mb', 'user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-A4',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Fix CRZ',
    description: 'Arborist needs to confirm CRZ for each retained tree',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-hafiz-mb', 'user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 3,
    notes: 'Arborist consultation required',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-A5',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Net buildable, tree credits, retained, replacement',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-hafiz-mb', 'user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-A6',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Retained trees near side-walk edge requires damage prevention during development',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Subplat & Engineering',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    notes: 'Arborist',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-A7',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Windthrow potential analysis & mitigation',
    description: 'KMC 18.57.085.',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Subplat & Engineering',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 3,
    notes: 'Arborist',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-A8',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Statement for retained tree identificantion & marking',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Subplat & Engineering',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    notes: 'Arborist',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P5-1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Add vicinity Map',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P5-2',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Add row dedication for each road & calculate size after row',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P5-3',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Update setbacks',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P5-4',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Lot 2 needs 10 on the east',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P4-1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Lot 3 needs pedestrian entrance - 75th ave is not accessible due to drainage facility',
    description: 'Guidance',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P5-7',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Remove unnecessary markings',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Low',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P8-1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Page 8-1: Remove trees of ROW from tree credit calculation',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P8-2',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Page 8: Use city tree protection fencing details',
    description: '18.57.090A',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P83',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Page 8: CRZ fix',
    description: 'Guidance',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P84',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Page 8: Major impact on trees near sidewalk',
    description: 'Guidance',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 3,
    notes: 'Arborist, Istiaque',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P85',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Page 8: Where is tree 123?',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    notes: 'Arborist, Istiaque',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P86-1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Page 8: 123 or 124 - are they signifcant?',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    notes: 'Arborist, Istiaque',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P86-2',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Page 8: Offsite on north-east corner.. CRZ in feet? Increase to 10 feet',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-istiaque-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    notes: 'Arborist, Istiaque',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P10-1',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Remove ROW trees from tree credit calculations',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 1,
    notes: 'Arborist',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-P10-2',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Fix lot sizes in the arborist report (118 in Lot 3 but should go Lot 1) 119 may be too',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'High',
    complexity: 'Low',
    category: 'Subplat & Engineering',
    assigneeIds: [],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    notes: 'Arborist',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
  {
    id: 'K7510-MASUD',
    organizationId: ORG_ID,
    projectId: PROJECT_ID,
    title: 'Can we save 105? It is in Lot 2.',
    description: '',
    type: 'Feature',
    status: 'Not Started',
    priority: 'Medium',
    category: 'Subplat & Engineering',
    assigneeIds: ['user-masud-mb'],
    parentTaskIds: [],
    subtaskIds: [],
    epicId: 'epic-k7510-subplat',
    estimatedDays: 2,
    notes: 'Arborist',
    timeLogs: [],
    standupNotes: [],
    createdAt: '2026-02-15T00:00:00Z',
    updatedAt: '2026-02-15T00:00:00Z',
    customerVisible: false,
    attachments: [],
    verificationLinks: [],
    externalLinks: [],
    changeLogs: [],
  },
];

// Update sprint with task IDs
kenmore7510SubplatSprint.taskIds = kenmore7510SprintTasks.map(t => t.id);
